import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip, Stack, Divider } from '@mui/material';
import {
    WorkspacePremium as PremiumIcon,
    Login as LoginIcon,
    PersonAddAlt1 as SignUpIcon,
    AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import RequestAccessForm from './RequestAccessForm';

/**
 * AUTH-7 / AUTH-13 — the "not-entitled" state: a feature that EXISTS but the
 * current tier cannot reach. This is deliberately distinct from a "not-built"
 * (Coming Soon) state — that means the feature does not exist yet. Rendering this
 * screen always means "exists, but requires a higher tier."
 *
 * Behavior depends on the viewer:
 *   - Anonymous (not signed in): the primary CTA is Sign in — a returning paid
 *     account just needs to authenticate. Anonymous capability stays login-free
 *     (ADR-007); only these paid pages route here.
 *   - Signed-in free: entitlements are granted via Clerk publicMetadata by an
 *     administrator (Stripe self-serve billing is deferred, ADR-007), so we say so
 *     honestly rather than dangle a checkout that does not exist yet.
 *
 * UI-only: the server (requireTier/requireAdmin) is the real boundary.
 */
export default function UpgradeScreen({
    feature = 'This feature',
    minTier = 'paid',
    isSignedIn = false,
    currentTier = 'free',
    userEmail = '',
}) {
    const isAdminGate = minTier === 'admin';
    const AccentIcon = isAdminGate ? AdminIcon : PremiumIcon;
    const accent = isAdminGate ? '#90caf9' : '#ffd54f';
    const title = isAdminGate ? 'Admin access required' : 'Premium feature';

    return (
        <Box
            sx={{
                minHeight: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
            }}
        >
            <Paper
                sx={{
                    p: 4,
                    maxWidth: 460,
                    width: '100%',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    textAlign: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <AccentIcon sx={{ color: accent, fontSize: 30 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
                        {title}
                    </Typography>
                    {!isAdminGate && (
                        <Chip
                            label="Premium"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', backgroundColor: 'rgba(255,213,79,0.15)', color: '#ffd54f' }}
                        />
                    )}
                </Box>

                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{feature}</Box>{' '}
                    {isAdminGate
                        ? 'is limited to administrators.'
                        : 'is part of the premium plan — persisted history, MSA / GRR studies, and formal validation evidence.'}
                </Typography>

                {!isSignedIn ? (
                    <>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                            Already have an account? Sign in to continue.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
                            <Button
                                component={RouterLink}
                                to="/sign-in"
                                variant="contained"
                                startIcon={<LoginIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Sign in
                            </Button>
                            <Button
                                component={RouterLink}
                                to="/sign-up"
                                variant="outlined"
                                startIcon={<SignUpIcon />}
                                sx={{ textTransform: 'none', color: '#90caf9', borderColor: 'rgba(144,202,249,0.5)' }}
                            >
                                Create account
                            </Button>
                        </Stack>
                        {!isAdminGate && (
                            <>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                    Or request access directly
                                </Typography>
                                <RequestAccessForm feature={feature} userEmail={userEmail} compact />
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                            {isAdminGate
                                ? 'Your account does not have administrator access.'
                                : `Your account is on the ${currentTier} plan — request access below and we will set you up.`}
                        </Typography>
                        {!isAdminGate && <RequestAccessForm feature={feature} userEmail={userEmail} />}
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="outlined"
                            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', alignSelf: 'center' }}
                        >
                            Back to Cockpit
                        </Button>
                    </>
                )}
            </Paper>
        </Box>
    );
}
